import { weekMap } from 'constants/course/batchScheduledCourse.constant';
import {
  READ_STATUS,
  MESSAGE_TYPE_NAME,
  MESSAGE_TYPE_AVATAR,
  MESSAGE_TYPE,
} from 'constants/message';
import { dateFormate } from 'constants/teacherRecruitment';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/routeConfig';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Divider,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// components
import {
  IconButtonAnimate,
  Iconify,
  MenuPopover,
  Scrollbar,
} from '@onedesign/ui';
import { useNotificationService } from 'services/NotificationService';
import { useClaimNotification } from 'hooks/index';
import { INotification, TNotificationCourseContent } from 'types/notification';
import { fToNow, fToTime, getWeeklyCourseDeviceTime } from 'utils/formatTime';
import { format } from 'date-fns';
import { TCourseFrequency } from 'types/course';
import { t } from 'utils/i18n';
import {
  formatClaimBatchFrequencyText,
  parseNotificationContent,
} from 'utils/notification';

// ----------------------------NotificationItem---------------------------

const getCourseTime = (dayOfWeek: number, startTime: string) => {
  const deviceTime = getWeeklyCourseDeviceTime(dayOfWeek, startTime);

  return `週${weekMap[deviceTime.dayOfWeek as keyof typeof weekMap]} ${
    deviceTime.startTime
  }`;
};

interface NotificationItemProps {
  notification: INotification;
  /** 只有搶課類的通知會傳，其餘維持不可點 */
  onClick?: () => void;
}

const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const courseInfo = parseNotificationContent<TNotificationCourseContent>(
    notification.contentObject,
  );
  const courseTags = (courseInfo?.tags ?? []).map(item => item.name).join('/');

  const { avatar, title } = renderContent(notification, courseInfo, courseTags);
  return (
    <ListItemButton
      onClick={onClick}
      // ListItemButton 預設就有 hover 底色與 pointer，沒有 onClick 的列會假裝自己可點
      disableRipple={!onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!onClick && {
          cursor: 'default',
          '&:hover': { backgroundColor: 'transparent' },
        }),
        ...(notification.readStatus === READ_STATUS.READ && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            {notification.messageType !== MESSAGE_TYPE.courseReminder &&
              notification.createdAt && (
                <>
                  <Iconify
                    icon="eva:clock-outline"
                    sx={{ mr: 0.5, width: 16, height: 16 }}
                  />
                  {fToNow(notification.createdAt)}
                </>
              )}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

// ----------------------------------------------------------------------

function renderContent(
  notification: INotification,
  courseInfo: TNotificationCourseContent | undefined,
  courseTags: string,
) {
  const formatDate = (date: Date | string | number, formatString: string) => {
    return format(new Date(date), formatString);
  };

  const renderMessageContent = () => {
    // 解析不出 contentObject 時退回顯示後端原文 —— 下面的日期格式化會吃到 Invalid Date
    if (!courseInfo)
      return (
        <Typography
          component="span"
          variant="body2"
          sx={{ color: 'text.secondary' }}
        >
          {notification.content}
        </Typography>
      );

    const normalCourseDateAndTime = `${formatDate(
      courseInfo.startAt,
      dateFormate,
    )} ${fToTime(courseInfo.startAt)}~${fToTime(courseInfo.endAt)}`;

    const batchScheduledCourseDate = `${formatDate(
      courseInfo.startAt,
      dateFormate,
    )}-${formatDate(courseInfo.endAt, dateFormate)}`;

    const batchScheduledCourseTime = notification.batchCourseScheduleRequestId
      ? (courseInfo.courseFrequency ?? [])
          .map((item: TCourseFrequency, index: number) => {
            return `${getCourseTime(item.dayOfWeek, item.startTime)}${
              index === (courseInfo.courseFrequency ?? []).length - 1
                ? ''
                : ' | '
            }`;
          })
          .join('')
      : '';

    // 規格的 [學生姓名]。目前一張搶課單只有一位學生，但欄位是陣列，仍做串接
    const studentNames = (courseInfo.students ?? [])
      .map(student => student.name)
      .join('、');

    const TypographyStyle = {
      color: 'text.secondary',
      display: '-webkit-box',
      overflow: 'hidden',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '2',
    };
    switch (notification.messageType) {
      case MESSAGE_TYPE.courseCancel:
        if (notification.batchCourseScheduleRequestId) {
          return (
            <Typography component="span" variant="body2" sx={TypographyStyle}>
              {batchScheduledCourseDate} {batchScheduledCourseTime} {courseTags}
              {t('notification.classCancelled', '課程已取消')}
            </Typography>
          );
        } else {
          return (
            <Typography component="span" variant="body2" sx={TypographyStyle}>
              {normalCourseDateAndTime} {courseTags}
              {t('notification.classCancelled', '課程已取消')}
            </Typography>
          );
        }
      case MESSAGE_TYPE.batchCourseScheduleNotice:
        return (
          <Typography component="span" variant="body2" sx={TypographyStyle}>
            {`${batchScheduledCourseDate} ${batchScheduledCourseTime} ${courseTags} 已排課`}
          </Typography>
        );
      case MESSAGE_TYPE.courseScheduleNotice:
        return (
          <Typography component="span" variant="body2" sx={TypographyStyle}>
            {`${normalCourseDateAndTime} ${courseTags} 已排課`}
          </Typography>
        );
      /*
        搶單堂課：規格是 [日期] [起~迄] [學生姓名]：[課程標籤] 可搶課。
        不能走 default —— 後端的 content 只有課程名稱，不是這句話。
      */
      case MESSAGE_TYPE.claimCourse:
        return (
          <Typography component="span" variant="body2" sx={TypographyStyle}>
            {`${normalCourseDateAndTime} ${studentNames}：${courseTags} `}
            {t('notification.claimableCourse', '可搶課')}
          </Typography>
        );
      /*
        搶連續課：規格是 [起日] - [迄日]，每週 N 次，每次 O 分鐘。
        這一種的 content 是空字串，同樣不能走 default。
      */
      case MESSAGE_TYPE.claimBatchCourse:
        return (
          <Typography component="span" variant="body2" sx={TypographyStyle}>
            {`${batchScheduledCourseDate}，${formatClaimBatchFrequencyText(
              courseInfo,
            )}`}
          </Typography>
        );
      default:
        return (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: 'text.secondary' }}
          >
            {notification.content}
          </Typography>
        );
    }
  };

  const title = (
    <>
      <Typography variant="subtitle2">
        {MESSAGE_TYPE_NAME[notification.messageType]()}
      </Typography>
      {renderMessageContent()}
    </>
  );
  return {
    avatar: (
      <img
        alt={notification.messageType}
        src={MESSAGE_TYPE_AVATAR[notification.messageType]}
      />
    ),
    title,
  };
}
export default function NotificationsPopover() {
  const navigate = useNavigate();
  const { notifications } = useNotificationService();
  const { getCourseAskTab, markAsRead, goToCourseAskTab } =
    useClaimNotification();

  const totalUnRead = notifications?.filter(
    item => item.readStatus === READ_STATUS.UNREAD,
  ).length;

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  /**
   * 搶課通知：標已讀 → 關彈窗 → 導到對應分頁。
   *
   * 先關彈窗，否則它會跟著留在新頁面上。其餘類型不回傳 handler，列維持不可點。
   */
  const handleClaimNotificationClick = (notification: INotification) => {
    const tab = getCourseAskTab(notification.messageType);
    if (!tab) return undefined;

    return async () => {
      await markAsRead(notification.id);
      handleClose();
      goToCourseAskTab(tab);
    };
  };

  // const handleMarkAllAsRead = () => {
  //   setNotifications(
  //     notifications.map(notification => ({
  //       ...notification,
  //       isRead: false,
  //     })),
  //   );
  // };

  return (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
        css={{}}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">
              {t('layouts.dashboard.header.notificationPopover.notify', '通知')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'layouts.dashboard.header.notificationPopover.totalUnread',
                '您有 {{totalUnRead}} 則未讀訊息',
                { totalUnRead },
              )}
            </Typography>
          </Box>

          {/* {totalUnRead > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButtonAnimate
                color="primary"
                onClick={handleMarkAllAsRead}
                css={{}}
              >
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )} */}
        </Box>

        <Divider sx={{ borderStyle: 'solid' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {notifications.slice(0, 5).map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleClaimNotificationClick(notification)}
              />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'solid' }} />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            disableRipple
            onClick={() => navigate(PATH.NOTIFICATION, { replace: true })}
          >
            {t(
              'layouts.dashboard.header.notificationPopover.viewAll',
              '查看全部',
            )}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
